import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import en from "./locales/en.json";

/**
 * VentureKhoj™ i18n bootstrap.
 *
 *   Supported:   en (default), hi
 *   Persist:     localStorage key `venturekhoj_lang`
 *                User-profile `language` (synced on login / profile save)
 *
 * R109.28 — perf: only English translations ship with the initial bundle.
 * Hindi (`hi.json` ≈ 102 KB raw / ~28 KB gz) is dynamically imported ONLY
 * when the user actually needs Hindi — either the detector settles on `hi`
 * on first paint, or the user later switches via LanguageSwitcher.
 *
 * This cuts ~20-25 KB gz off main.js for the ~80%+ of visitors on English.
 */
export const SUPPORTED_LANGS = ["en", "hi"];
export const LS_LANG_KEY = "venturekhoj_lang";

const _hindiLoad = { promise: null, done: false };
export function ensureHindiLoaded() {
  if (_hindiLoad.done) return Promise.resolve();
  if (!_hindiLoad.promise) {
    _hindiLoad.promise = import(
      /* webpackChunkName: "i18n-hi" */ "./locales/hi.json"
    ).then((mod) => {
      i18n.addResourceBundle("hi", "translation", mod.default || mod, true, true);
      _hindiLoad.done = true;
      // R109.32 — `addResourceBundle` alone does NOT trigger subscribed React
      // components (via `useTranslation`) to re-render. If the user's current
      // language is Hindi at the time the bundle arrives, emit `languageChanged`
      // so react-i18next re-renders every subscriber with the freshly available
      // translations. Without this, users see English fallback until the next
      // language change or route navigation.
      if ((i18n.language || "").toLowerCase().startsWith("hi")) {
        i18n.emit("languageChanged", i18n.language);
      }
    }).catch(() => {
      // Network hiccup — reset so the next call can retry.
      _hindiLoad.promise = null;
    });
  }
  return _hindiLoad.promise;
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      // `hi` intentionally omitted here — loaded on demand via ensureHindiLoaded().
    },
    fallbackLng: "en",
    supportedLngs: SUPPORTED_LANGS,
    interpolation: { escapeValue: false }, // React already escapes
    detection: {
      order: ["localStorage", "navigator", "htmlTag"],
      lookupLocalStorage: LS_LANG_KEY,
      caches: ["localStorage"],
    },
    // Keys are missing in `hi` at boot — silence i18next's noisy warning.
    saveMissing: false,
    missingKeyHandler: () => {},
  });

/**
 * Kick off Hindi load in the background when the user's resolved language is
 * Hindi. English is the eager bundle so English users pay zero extra bytes.
 * For Hindi users, we fetch immediately (NOT via ric) so the swap from
 * English fallback → Hindi happens in <300 ms on typical mobile networks —
 * fast enough that most users won't perceive a flash. This tradeoff wins us
 * ~27 KB gz off first-paint JS for the 80%+ of visitors on English.
 */
function schedulePreloadIfHindi() {
  const lang = (i18n.language || "").toLowerCase();
  if (!lang.startsWith("hi")) return;
  // Fetch NOW — no ric — so Hindi swaps in as fast as the network allows.
  ensureHindiLoaded();
}

schedulePreloadIfHindi();

// Reflect current lang on <html>, and lazy-load Hindi if user switches to it.
i18n.on("languageChanged", (lng) => {
  if (typeof document !== "undefined") {
    document.documentElement.lang = lng;
  }
  if ((lng || "").toLowerCase().startsWith("hi")) {
    ensureHindiLoaded();
  }
});

export default i18n;
